/**
 * Free gift sync — single pipeline, no fetch hooks.
 * cart-items updates UI once; gift logic only mutates when needed.
 */

const GIFT_PROP = "_is_free_gift";
const ROOT = () => window.Shopify?.routes?.root || "/";
const MAX_STEPS_PER_RUN = 3;

const nativeFetch = window.fetch.bind(window);

let debounceTimer = null;
let pipelineBusy = false;
let pipelineQueued = false;
let lastStableSnapshot = "";

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function getRewardConfig() {
  const c = window.Shopify?.customRewards;
  if (!c?.giftVariantId || !c?.giftThreshold) return null;
  const giftId = parseInt(String(c.giftVariantId), 10);
  const threshold = parseInt(String(c.giftThreshold), 10);
  if (!giftId || !threshold) return null;
  return { giftId, threshold };
}

function truthyGiftFlag(val) {
  return val === true || val === "true" || val === 1 || val === "1";
}

function isOurGiftLine(item, giftId) {
  return (
    item.variant_id === giftId &&
    item.properties &&
    truthyGiftFlag(item.properties[GIFT_PROP])
  );
}

function isGiftRelatedDiscountText(text) {
  const t = String(text || "").toLowerCase();
  return (
    t.includes("gift with purchase") ||
    t.includes("free gift") ||
    t.includes("gift reward") ||
    t.includes("подарунок")
  );
}

function isIgnoredCartLevelDiscount(d) {
  if (d.target_type === "shipping_line") return true;
  return isGiftRelatedDiscountText(`${d.title || ""} ${d.description || ""}`);
}

function isIgnoredLineDiscount(entry) {
  const app = entry.discount_application || entry;
  return isGiftRelatedDiscountText(
    `${entry.title || ""} ${app.title || ""} ${app.description || ""}`,
  );
}

function hasActiveBundle(cart, giftId) {
  const tags = [];
  for (const item of cart.items || []) {
    if (item.variant_id === giftId) continue;
    for (const [key, value] of Object.entries(item.properties || {})) {
      if (value == null || value === "") continue;
      const k = key.toLowerCase();
      if (k.includes("bundle") || k.includes("bndlr")) {
        tags.push(String(value));
      }
    }
  }
  if (tags.length < 2) return false;
  const counts = {};
  for (const t of tags) {
    counts[t] = (counts[t] || 0) + 1;
    if (counts[t] >= 2) return true;
  }
  return false;
}

function hasCustomerPromotions(cart, giftId) {
  if ((cart.discount_codes || []).some((d) => d.applicable)) return true;
  if (
    (cart.cart_level_discount_applications || []).some(
      (d) => !isIgnoredCartLevelDiscount(d),
    )
  ) {
    return true;
  }
  for (const item of cart.items || []) {
    if (item.variant_id === giftId) continue;
    if (
      (item.line_level_discount_allocations || []).some(
        (a) => !isIgnoredLineDiscount(a),
      )
    ) {
      return true;
    }
    if ((item.discounts || []).some((d) => !isIgnoredLineDiscount(d))) {
      return true;
    }
  }
  return hasActiveBundle(cart, giftId);
}

/**
 * Gift variant already in cart (e.g. $0 via Shopify automatic discount).
 * Do not cart/add.js again — that creates duplicate lines.
 */
function evaluateAction(cart) {
  const cfg = getRewardConfig();
  if (!cfg) return { type: "noop" };

  const { giftId, threshold } = cfg;
  const shouldHave =
    cart.total_price >= threshold && !hasCustomerPromotions(cart, giftId);

  const ourGift = (cart.items || []).find((i) => isOurGiftLine(i, giftId));
  const hasGiftVariant = (cart.items || []).some(
    (i) => i.variant_id === giftId,
  );

  if (shouldHave) {
    if (ourGift) {
      if (ourGift.quantity !== 1) {
        return { type: "fix", lineKey: ourGift.key };
      }
      return { type: "noop" };
    }
    if (hasGiftVariant) {
      return { type: "noop" };
    }
    return { type: "add", giftId };
  }

  const giftLines = (cart.items || []).filter((i) => i.variant_id === giftId);
  if (giftLines.length > 0) {
    return { type: "remove", lineKey: giftLines[0].key };
  }

  return { type: "noop" };
}

function snapshotCart(cart) {
  return JSON.stringify({
    t: cart.total_price,
    keys: (cart.items || []).map((i) => `${i.key}:${i.quantity}`).join("|"),
    dc: (cart.discount_codes || [])
      .map((d) => `${d.code}:${d.applicable}`)
      .join(","),
  });
}

function getRequiredSections() {
  const ids = [];
  const mainCart = document.querySelector("main-cart");
  const miniCart = document.querySelector("cart-drawer");
  if (mainCart?.sectionId) ids.push(mainCart.sectionId);
  if (miniCart?.sectionId) ids.push(miniCart.sectionId);
  const bubble = document.getElementById("cart-icon-bubble");
  ids.push(bubble ? "cart-icon-bubble" : "header");
  return ids;
}

async function loadCartJson() {
  return (await nativeFetch(`${ROOT()}cart.js`)).json();
}

async function loadCartWithSections() {
  const sectionIds = getRequiredSections();
  const cart = await loadCartJson();
  cart.sections = await (
    await nativeFetch(`${ROOT()}?sections=${sectionIds.join(",")}`)
  ).json();
  return cart;
}

function publishCartToTheme(cart) {
  if (!window.theme?.pubsub) return;
  theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
    source: "gift-logic",
    cart,
  });
}

async function applyCartMutation(action) {
  if (action.type === "add") {
    const res = await nativeFetch(`${ROOT()}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [
          {
            id: action.giftId,
            quantity: 1,
            properties: { [GIFT_PROP]: true },
          },
        ],
      }),
    });
    if (!res.ok) throw new Error(`gift add failed ${res.status}`);
    return;
  }

  const res = await nativeFetch(`${ROOT()}cart/change.js`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id: action.lineKey,
      quantity: action.type === "remove" ? 0 : 1,
    }),
  });
  if (!res.ok) {
    console.warn("[gift-logic] cart/change rejected", res.status);
  }
}

async function runPipeline() {
  if (pipelineBusy) {
    pipelineQueued = true;
    return;
  }

  pipelineBusy = true;
  let didMutate = false;

  try {
    for (let step = 0; step < MAX_STEPS_PER_RUN; step++) {
      const cart = await loadCartJson();
      const action = evaluateAction(cart);

      if (action.type === "noop") {
        lastStableSnapshot = snapshotCart(cart);
        break;
      }

      didMutate = true;
      await applyCartMutation(action);
      await sleep(200);
    }

    if (didMutate) {
      const cart = await loadCartWithSections();
      lastStableSnapshot = snapshotCart(cart);
      publishCartToTheme(cart);
    }
  } catch (e) {
    console.error("[gift-logic]", e);
  } finally {
    pipelineBusy = false;
    if (pipelineQueued) {
      pipelineQueued = false;
      schedulePipeline(250);
    }
  }
}

function schedulePipeline(delayMs = 400) {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(runPipeline, delayMs);
}

function handleCartUpdate(event) {
  if (event.source === "gift-logic") return;
  if (!getRewardConfig()) return;

  const cart = event.cart;
  if (!cart) {
    schedulePipeline(400);
    return;
  }

  const action = evaluateAction(cart);
  const snap = snapshotCart(cart);

  if (event.source === "cart-items") {
    lastStableSnapshot = snap;
    if (action.type === "noop") {
      return;
    }
    schedulePipeline(120);
    return;
  }

  schedulePipeline(event.source === "cart-discount" ? 400 : 350);
}

if (window.theme?.pubsub) {
  theme.pubsub.subscribe(
    theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
    handleCartUpdate,
  );
}

function initBundlerButtons() {
  document.querySelectorAll(".bndlr-add-to-cart").forEach((btn) => {
    if (btn.dataset.giftPipelineBound) return;
    btn.dataset.giftPipelineBound = "1";
    btn.addEventListener("click", () => schedulePipeline(900));
  });
}

function initBundleSwiper() {
  if (document.querySelector(".swiper-initialized")) return;
  const container = document.querySelector(".bundler-widgets-side-by-side");
  if (!container) return;

  container.classList.add("swiper");
  const inner = container.querySelector(".side-by-side-inner-element");
  if (inner) inner.classList.add("swiper-wrapper");
  container
    .querySelectorAll(".bundler-target-element.bndlr-automatic")
    .forEach((slide) => slide.classList.add("swiper-slide"));

  const pagination = document.createElement("div");
  pagination.className = "swiper-pagination bundle-pagination";
  container.appendChild(pagination);

  if (typeof Swiper !== "undefined") {
    new Swiper(container, {
      slidesPerView: 1,
      spaceBetween: 20,
      pagination: { el: ".bundle-pagination", clickable: true },
      breakpoints: { 990: { slidesPerView: 1.2 }, 768: { slidesPerView: 1.7 } },
    });
  }
}

const bundleObserver = new MutationObserver(() => {
  if (document.querySelector(".bndlr-container")) {
    initBundleSwiper();
    initBundlerButtons();
  }
});

bundleObserver.observe(document.body, { childList: true, subtree: true });
